/* ---------------------------------------------------------------------------
   Every rating on record, and what each one concluded.

   THE APPEND-ONLY LIST. Two ratings of the same structure both stay, and one may
   be marked superseded — the point is that somebody drove over that bridge under
   the posting the earlier one produced, and the record of what was believed at
   the time is what makes the later one defensible.

   WHAT THIS SCREEN IS FOR, in one sentence: which structures are posted, and on
   whose signature. That is one indexed query — see the `ratings_posting_idx`
   partial index in migrations/0025_ratings.sql — rather than a walk over the
   register asking each structure in turn.
   --------------------------------------------------------------------------- */

import { h } from '../../core/dom.ts';
import { button, card, cardHead, emptyState, fact, tag, type TagRank } from '../../components/widgets.ts';
import { displayRf, displayTonnes } from '../../cbplr/index.ts';
import type { RatingRecord } from '../api.ts';
import { actions } from '../actions.ts';
import { can, fieldsFor, FAMILIES, whyNot, type RatingFamily, type State } from '../store.ts';
import { ratingSheet } from './ratingCharts.ts';
import { onSupersededModel, shortRevision } from '../../data/phase.ts';
import { governingRating } from '../../data/posting.ts';
import { NO_LOSS, assumedDamage, assumedDamageLine } from '../damage.ts';
/* TEMPORARY — the FWO demonstration. Delete with src/data/fwo-demo.ts. */
import { isDemoRating } from '../../data/fwo-demo.ts';

const STATE_RANK: Readonly<Record<RatingRecord['state'], TagRank>> = {
  draft: 'none',
  review: 2,
  accepted: 1,
  superseded: 'none',
};

const STATE_NOTE: Readonly<Record<RatingRecord['state'], string>> = {
  draft: 'Arithmetic. Nobody has stood behind it yet.',
  review: 'Sent for review, not yet accepted.',
  accepted: 'A decision of record. This is what the posting sign is made from.',
  superseded: 'Replaced by a later rating. Kept, because it was once acted on.',
};

/** How many values this record's form asked for, or the sources' own count for a family this build has no form for. */
function fieldCount(r: RatingRecord): number {
  if (!(FAMILIES as readonly string[]).includes(r.family)) return Object.keys(r.sources).length;
  const position = r.position === 'interior' || r.position === 'exterior' ? r.position : null;
  return fieldsFor(r.family as RatingFamily, position).length;
}

/** The name of the structure, where the register is loaded. */
function nameOf(s: Readonly<State>, bridgeId: string): string {
  return s.bridges?.find((b) => b.id === bridgeId)?.name ?? bridgeId;
}

export function ratingsScreen(s: Readonly<State>): HTMLElement {
  if (s.ratings === null) {
    return h(
      'div',
      { class: 'rt-scr' },
      card({ pad: true }, emptyState(
        s.backend === 'live' ? 'Reading the ratings.' : 'Nothing can be read until the deployment answers.',
      )),
    );
  }

  /* With a structure in hand, only its ratings; the whole register only when none is. */
  const shown = s.selected === null ? s.ratings : s.ratings.filter((r) => r.bridgeId === s.selected);
  const posted = shown.filter((r) => r.state === 'accepted' && r.postingRequired);
  /* The rating the address opened (LC-31), else the one the structure stands on. */
  const current =
    shown.find((r) => r.id === s.opened) ?? (s.selected === null ? null : currentRating(s.ratings, s.selected));

  return h(
    'div',
    { class: 'rt-scr' },
    current === null || current.result === null ? null : currentSheet(s, current),

    posted.length > 0
      ? card(
          { pad: true, class: 'rt-alert' },
          cardHead('Posting required', `${posted.length} ACCEPTED RATING${posted.length === 1 ? '' : 'S'}`),
          h(
            'div',
            { class: 'rt-tbl rt-tbl--post' },
            h(
              'div',
              { class: 'rt-tbl__head' },
              h('span', { text: 'Structure' }),
              h('span', { text: 'Governing' }),
              h('span', { text: 'Posting' }),
              h('span', { text: 'Accepted by' }),
            ),
            ...posted.map((r) =>
              h(
                'div',
                { class: 'rt-tbl__row' },
                h('span', null, h('strong', { text: nameOf(s, r.bridgeId) }), h('span', { class: 'rt-tbl__sub', text: r.bridgeId })),
                h('span', {
                  text:
                    r.governingRf === null
                      ? '—'
                      : `RF ${displayRf(r.governingRf)} · ${r.governingMethod ?? ''} ${r.governingLevel ?? ''}`,
                }),
                h(
                  'span',
                  null,
                  ...(r.result?.posting ?? [])
                    .filter((p) => !p.unrestricted)
                    .map((p) => tag(`${p.vehicle.name} ${displayTonnes(Math.max(0, p.tonnes))} t`, 4)),
                ),
                h('span', { class: 'rt-tbl__sub', text: r.acceptedBy ?? '—' }),
              ),
            ),
          ),
        )
      : null,

    shown.length === 0
      ? card({ pad: true }, emptyState(s.selected === null ? 'No structure has been rated yet.' : 'This structure has never been rated.'))
      : card(
          { pad: true },
          s.selected === null
            ? cardHead('All ratings', String(shown.length))
            : cardHead('Ratings of this structure', String(shown.length)),
          ...shown.map((r) => ratingRecord(s, r)),
        ),
  );
}

/**
 * The rating a structure is standing on: the GOVERNING one (LC-22) — the
 * lowest accepted factor across its spans and girder positions, so a later
 * interior-girder rating cannot hide a worse exterior one. Where nothing is
 * accepted, the newest rating not superseded, which is not yet what any
 * posting sign was made from.
 */
export function currentRating(ratings: readonly RatingRecord[], bridgeId: string): RatingRecord | null {
  const governing = governingRating(ratings, bridgeId);
  if (governing !== null) return governing;
  const mine = ratings.filter((r) => r.bridgeId === bridgeId && r.state !== 'superseded');
  return mine.reduce<RatingRecord | null>((a, r) => (a === null || r.ratedAt > a.ratedAt ? r : a), null);
}

/** The defects and inspection a rating's damage inputs rest on, as one line (LC-20). */
export function damageBasisLine(r: RatingRecord): string {
  const defects = r.defectIds ?? [];
  const none = Object.values(r.damageBasis ?? {}).some((b) => b === NO_LOSS);
  const parts = [
    defects.length > 0 ? `issue${defects.length === 1 ? '' : 's'} ${defects.join(', ')}` : null,
    none ? 'none found' : null,
    r.inspectionId ? `inspection ${r.inspectionId}` : null,
  ].filter(Boolean);
  return parts.length === 0 ? '—' : parts.join(' · ');
}

/** A rating drawn — the one a structure stands on, or the one opened — on the Ratings screen and in the property panel alike. */
function currentSheet(s: Readonly<State>, r: RatingRecord): HTMLElement {
  return ratingSheet(
    s,
    `Load rating — ${nameOf(s, r.bridgeId)}`,
    undefined,
    {
      result: r.result!,
      signed: s.bridges?.find((b) => b.id === r.bridgeId)?.loadRestriction ?? null,
    },
  );
}

/** One rating on record: what it concluded, on whose signature, and its state. */
function ratingRecord(s: Readonly<State>, r: RatingRecord): HTMLElement {
  /* TEMPORARY — a demonstration rating has no row to move. */
  const fixed = isDemoRating(r);
  /* LC-12: made on a model that is no longer the bridge's model of record. */
  const superseded = supersededModel(s, r);
  /* LC-21: what the deployment would refuse acceptance on, said on the button. */
  const assumed = assumedDamageLine(assumedDamage(r.family, r.sources, r.damageBasis ?? {}, r.inspectionId ?? null));
  const governs = r.state === 'accepted' && s.ratings !== null && governingRating(s.ratings, r.bridgeId)?.id === r.id;
  return h(
    'div',
    { class: 'rt-rec' },
    h(
      'div',
      { class: 'rt-rec__head' },
      h('strong', { text: nameOf(s, r.bridgeId) }),
      h('span', { class: 'rt-tbl__sub', text: `${r.family} ${r.continuity}` }),
      h('span', { class: 'spacer' }),
      tag(r.state, STATE_RANK[r.state], STATE_NOTE[r.state]),
      r.postingRequired ? tag('posting required', 4) : tag('unrestricted', 1),
      governs ? tag('governing', 4, 'The lowest accepted rating factor across this structure’s spans and girders.') : null,
      r.state === 'review' && assumed !== null ? tag('damage assumed', 3, assumed) : null,
      superseded
        ? tag('made on a superseded model', 3, 'The bridge’s model of record, or its revision, has changed since this rating was made.')
        : null,
    ),
    h(
      'div',
      { class: 'rt-facts rt-facts--tight' },
      fact('Governing RF', r.governingRf === null ? '—' : displayRf(r.governingRf)),
      fact('Method', r.governingMethod ?? '—'),
      fact('Level', r.governingLevel ?? '—'),
      fact('Section', r.governingCheck ?? '—'),
      fact('Rated by', r.ratedBy ?? 'not identified'),
      fact('Rated at', r.ratedAt),
      /* The three columns that make the number reproducible. On the
         face of the record and not behind a disclosure, because a
         rating whose provenance is one click away is a rating nobody
         checks. */
      fact('Profile', `${r.profileId} ${r.profileVersion}`),
      fact('Engine', r.engineVersion),
      /* Which model it was made on (LC-12), the revision a reader checks against. */
      fact('Model', r.modelId ? (r.modelRevision ? `${r.modelId} · ${shortRevision(r.modelRevision)}` : r.modelId) : '—'),
      /* The members it rates, off the model's component directory (migration 0049). */
      fact('Rates', r.componentIds?.length ? `${r.componentIds.length} component${r.componentIds.length === 1 ? '' : 's'} of the model` : '—'),
      /* LC-20: the report names the defects its damage inputs came from. */
      fact('Damage from', damageBasisLine(r)),
      fact('Member', [r.spanIndex === null ? null : `Span ${r.spanIndex}`, r.position].filter(Boolean).join(' · ') || '—'),
      r.supersededBy ? fact('Superseded by', r.supersededBy) : null,
      fact(
        'Attributed values',
        /* Counted against the form this family and girder actually asked,
           not a fixed eighteen — which was the flat slab's length. */
        `${Object.keys(r.sources).length} of ${fieldCount(r)}`,
      ),
    ),
    r.note === null ? null : h('p', { class: 'rt-rec__note', text: r.note }),
    h(
      'div',
      { class: 'rt-btnrow' },
      button('Send for review', {
        sm: true,
        disabled: fixed || r.state !== 'draft' || s.busy || !can(s, 'rating.compute'),
        onClick: () => void actions.moveState(r.id, 'review'),
      }),
      button('Accept', {
        sm: true,
        disabled: fixed || r.state !== 'review' || s.busy || !can(s, 'rating.accept') || assumed !== null,
        ...(!can(s, 'rating.accept')
          ? { title: whyNot(s, 'accept a rating') }
          : assumed !== null
            ? { title: assumed }
            : {}),
        onClick: () => void actions.moveState(r.id, 'accepted'),
      }),
      button('Return to draft', {
        sm: true,
        disabled: fixed || r.state !== 'review' || s.busy || !can(s, 'rating.accept'),
        onClick: () => void actions.moveState(r.id, 'draft'),
      }),
      /* Superseding is offered only on an accepted rating, because
         that is the only state the lattice allows it from — see
         TRANSITIONS in functions/lib/ratings.ts. There is no delete
         anywhere in this application. */
      /* Its sheet at the head of the Ratings screen, with an address of its own (LC-31). */
      button(s.opened === r.id ? 'Opened' : 'Open', {
        sm: true,
        disabled: r.result === null || s.opened === r.id,
        title: 'Draw this rating at the head of the Ratings screen',
        onClick: () => actions.openRecord(r.id),
      }),
      button('Supersede', {
        sm: true,
        danger: true,
        disabled: fixed || r.state !== 'accepted' || s.busy || !can(s, 'rating.accept'),
        title: 'Mark this rating as replaced. It stays on the record.',
        onClick: () => void actions.moveState(r.id, 'superseded'),
      }),
    ),
  );
}

/** Whether a rating was made on a model that is no longer its bridge's model of record. */
export function supersededModel(s: Readonly<State>, r: RatingRecord): boolean {
  return onSupersededModel(r.modelId, s.bridges?.find((b) => b.id === r.bridgeId)?.modelOfRecord, r.modelRevision);
}

/**
 * THE RATINGS OF ONE STRUCTURE, for the page's property panel when the
 * explorer's Ratings on record is picked. The same records the Ratings screen
 * lists, newest first, narrowed to the structure in hand.
 */
export function structureRatings(s: Readonly<State>, bridgeId: string | null): HTMLElement {
  if (s.ratings === null) {
    return h(
      'div',
      { class: 'rt-props' },
      card({ pad: true }, emptyState(
        s.backend === 'live' ? 'Reading the ratings.' : 'Nothing can be read until the deployment answers.',
      )),
    );
  }
  const mine = bridgeId === null ? [] : s.ratings.filter((r) => r.bridgeId === bridgeId);
  const current = bridgeId === null ? null : currentRating(s.ratings, bridgeId);
  return h(
    'div',
    { class: 'rt-props' },
    current === null || current.result === null ? null : currentSheet(s, current),
    mine.length === 0
      ? card({ pad: true }, emptyState('This structure has never been rated.'))
      : card(
          { pad: true },
          cardHead('Ratings of this structure', String(mine.length)),
          ...mine.map((r) => ratingRecord(s, r)),
        ),
  );
}
